import { useState, useEffect } from "react";
import {
  Users,
  X,
  Search,
  Filter,
  Mail,
  Phone,
  Calendar,
  Badge,
  Building2,
  UserPlus,
  Download,
  Eye,
  Edit,
  MoreHorizontal,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { Badge as UIBadge } from "../../../../components/ui/badge";
import { Avatar, AvatarFallback } from "../../../../components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../../../../components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import { Card, CardContent } from "../../../../components/ui/card";
import { cn } from "../../../../lib/utils";
import { employeesAPI } from "@/lib/api";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { useToast } from "@/hooks/use-toast";

export default function EmployeesModal({
  department,
  isOpen,
  onClose,
  onViewEmployee,
  onEditEmployee,
}) {
  const { toast } = useToast();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("nom");
  const [filterBy, setFilterBy] = useState("all");
  const [viewMode, setViewMode] = useState("list");

  // Fetch real data from API
  useEffect(() => {
    const fetchEmployees = async () => {
      if (isOpen && department?.id) {
        setLoading(true);
        try {
          const response = await employeesAPI.getAll({
            departement_id: department.id,
            limit: 100, // Load more for the modal view
          });
          setEmployees(response.data.data.employees || []);
        } catch (error) {
          console.error("Error fetching employees:", error);
          toast({
            title: "Erreur",
            description: "Impossible de charger la liste des employés",
            variant: "destructive",
          });
        } finally {
          setLoading(false);
        }
      }
    };

    fetchEmployees();
  }, [isOpen, department?.id]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "MAD",
      notation: "standard",
    }).format(amount);
  };

  const formatDate = (date) => {
    return new Intl.DateTimeFormat("fr-FR", {
      year: "numeric",
      month: "long",
      day: "numeric",
    }).format(new Date(date));
  };

  const getContractColor = (type) => {
    const colors = {
      cdi: "bg-green-100 text-green-800",
      cdl: "bg-green-100 text-green-800",
      cdd: "bg-blue-100 text-blue-800",
      stage: "bg-orange-100 text-orange-800",
      freelance: "bg-purple-100 text-purple-800",
    };
    return colors[type?.toLowerCase()] || "bg-gray-100 text-gray-800";
  };

  const getStatusColor = (status) => {
    const colors = {
      actif: "bg-green-100 text-green-800",
      inactif: "bg-gray-100 text-gray-800",
      conge: "bg-yellow-100 text-yellow-800",
      suspendu: "bg-red-100 text-red-800",
    };
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800";
  };

  // Filter and sort employees
  const filteredEmployees = employees
    .filter((emp) => {
      const matchesSearch =
        (emp.prenom?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (emp.nom?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (emp.email?.toLowerCase() || "").includes(searchTerm.toLowerCase()) ||
        (emp.role?.toLowerCase() || "").includes(searchTerm.toLowerCase());

      const matchesFilter =
        filterBy === "all" ||
        (filterBy === "actif" && emp.statut_employe === "actif") ||
        (filterBy === "CDI" && emp.type_contrat?.toLowerCase() === "cdi") ||
        (filterBy === "CDD" && emp.type_contrat?.toLowerCase() === "cdd") ||
        (filterBy === "Stage" && emp.type_contrat?.toLowerCase() === "stage");

      return matchesSearch && matchesFilter;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "nom":
          return (a.nom || "").localeCompare(b.nom || "");
        case "prenom":
          return (a.prenom || "").localeCompare(b.prenom || "");
        case "poste":
          return (a.role || "").localeCompare(b.role || "");
        case "date_embauche":
          return new Date(b.date_embauche || 0) - new Date(a.date_embauche || 0);
        case "salaire":
          return (parseFloat(b.salaire) || 0) - (parseFloat(a.salaire) || 0);
        default:
          return 0;
      }
    });

  if (!department) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Users className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <span>Employés - {department.nom}</span>
              <p className="text-sm text-gray-500 font-normal mt-1">
                {filteredEmployees.length} employé
                {filteredEmployees.length > 1 ? "s" : ""}
                {filteredEmployees.length !== employees.length &&
                  ` sur ${employees.length}`}
              </p>
            </div>
          </DialogTitle>
        </DialogHeader>

        {/* Filters and Search */}
        <div className="flex-shrink-0 space-y-4 border-b pb-4">
          <div className="flex flex-col sm:flex-row gap-4">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher par nom, email, poste..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Sort */}
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Trier par..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nom">Nom de famille</SelectItem>
                <SelectItem value="prenom">Prénom</SelectItem>
                <SelectItem value="poste">Poste</SelectItem>
                <SelectItem value="date_embauche">Date d'embauche</SelectItem>
                <SelectItem value="salaire">Salaire</SelectItem>
              </SelectContent>
            </Select>

            {/* Filter */}
            <Select value={filterBy} onValueChange={setFilterBy}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filtrer par..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="actif">Actifs uniquement</SelectItem>
                <SelectItem value="CDI">CDI</SelectItem>
                <SelectItem value="CDD">CDD</SelectItem>
                <SelectItem value="Stage">Stagiaires</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Quick Actions */}
          <div className="flex items-center justify-between">
        

            {/* View Toggle */}
            <div className="flex items-center space-x-1 bg-gray-100 rounded-lg p-1">
              <Button
                variant={viewMode === "list" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("list")}
                className="h-8 px-3"
              >
                Liste
              </Button>
              <Button
                variant={viewMode === "cards" ? "default" : "ghost"}
                size="sm"
                onClick={() => setViewMode("cards")}
                className="h-8 px-3"
              >
                Cartes
              </Button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : filteredEmployees.length === 0 ? (
            <div className="text-center py-12">
              <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500 text-lg">
                {searchTerm || filterBy !== "all"
                  ? "Aucun employé trouvé avec ces critères"
                  : "Aucun employé dans ce département"}
              </p>
              <p className="text-gray-400 text-sm">
                {searchTerm || filterBy !== "all"
                  ? "Essayez de modifier vos filtres de recherche"
                  : "Commencez par ajouter des employés à ce département"}
              </p>
            </div>
          ) : viewMode === "cards" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-1">
              {filteredEmployees.map((employee) => (
                <Card
                  key={employee.id}
                  className="hover:shadow-md transition-shadow"
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center space-x-3">
                        <Avatar className="h-10 w-10">
                          <AvatarFallback className="bg-blue-100 text-blue-600">
                            {employee.prenom[0]}
                            {employee.nom[0]}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <h4 className="font-medium text-gray-900">
                            {employee.prenom} {employee.nom}
                          </h4>
                          <p className="text-sm text-gray-600">
                            {employee.role}
                          </p>
                        </div>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                          >
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => onViewEmployee?.(employee)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            Voir profil
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onEditEmployee?.(employee)}
                          >
                            <Edit className="h-4 w-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex items-center text-gray-600">
                        <Mail className="h-3 w-3 mr-2" />
                        <span className="truncate">{employee.email}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Phone className="h-3 w-3 mr-2" />
                        <span>{employee.telephone}</span>
                      </div>
                      <div className="flex items-center text-gray-600">
                        <Calendar className="h-3 w-3 mr-2" />
                        <span>
                          Embauché le {formatDate(employee.date_embauche)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-4 pt-3 border-t">
                      <div className="flex items-center space-x-2">
                        <UIBadge
                          className={getContractColor(employee.type_contrat)}
                        >
                          {employee.type_contrat}
                        </UIBadge>
                        <UIBadge className={getStatusColor(employee.statut)}>
                          {employee.statut}
                        </UIBadge>
                      </div>
                      <span className="font-semibold text-gray-900">
                        {formatCurrency(employee.salaire)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredEmployees.map((employee) => (
                <div
                  key={employee.id}
                  className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center space-x-4 flex-1">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-blue-100 text-blue-600">
                        {employee.prenom?.[0]}
                        {employee.nom?.[0]}
                      </AvatarFallback>
                    </Avatar>

                    <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                      <div>
                        <h4 className="font-medium text-gray-900">
                          {employee.prenom} {employee.nom}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {employee.role}
                        </p>
                      </div>

                      <div className="text-sm text-gray-600">
                        <div className="flex items-center">
                          <Mail className="h-3 w-3 mr-1" />
                          <span className="truncate">{employee.email}</span>
                        </div>
                        <div className="flex items-center mt-1">
                          <Phone className="h-3 w-3 mr-1" />
                          <span>{employee.telephone}</span>
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <UIBadge
                          className={getContractColor(employee.type_contrat)}
                        >
                          {employee.type_contrat}
                        </UIBadge>
                        <UIBadge className={getStatusColor(employee.statut_employe)}>
                          {employee.statut_employe}
                        </UIBadge>
                      </div>

                      <div className="text-right">
                        <div className="font-semibold text-gray-900">
                          {formatCurrency(employee.salaire)}
                        </div>
                        <div className="text-sm text-gray-500">
                          {formatDate(employee.date_embauche)}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-1 ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onViewEmployee?.(employee)}
                      className="h-8 w-8 p-0"
                    >
                      <Eye className="h-4 w-4" />
                    </Button>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onViewEmployee?.(employee)}
                        >
                          <Eye className="h-4 w-4 mr-2" />
                          Voir profil
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onEditEmployee?.(employee)}
                        >
                          <Edit className="h-4 w-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
